import { ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { UsersContext } from 'contexts/users';
import { SessionContext } from 'contexts/session';
import { BrowserView, MobileView, isDesktop, isMobile } from 'react-device-detect';
import SearchFilter from './Search';
import UserMobileTopBar from 'components/usersMobileTopBar';
import Button from 'components/button/Button';
import { ActionModal } from 'components/modal';
import './userHeaderFilters.scss';
import Notification from 'components/notification';
import {
  resendUserInvites,
  setPendingInvitesAddon,
  setUserLicense,
} from 'components/usersTable/actions';
import { SelectedCounts, Title } from 'components/layout/tableContentHeader/styles';
import InviteUsers from 'pages/Users/components/InviteUsers';
import { Invite } from 'pages/Users/components/InviteUsers/AddByEmails/invitesReducer';
import { isTeamManagerOrAccountOwner, validateAccountOwner } from 'contexts/session/utilities';
import { FEATURE_FLAGS, useBooleanFlag } from 'hooks/useFeatureFlags';
import useLicenseRejection from 'hooks/useLicenseRejection';
import useLicenseAvailability from 'hooks/useLicenseAvailability';
import { findAssignedSkillDive } from 'hooks/useSkillDiveToggleState';
import { CONTENT_TYPES } from 'constants/playlists';
import { ACTION_MODAL_BUTTON_TYPES, ACTION_MODAL_LABELS } from 'constants/components/actionModal';
import { NOTIFICATION_TYPES } from 'constants/components/notification';
import {
  LICENSES_NO_LONGER_CHANGEABLE,
  LICENSES_UPDATED,
  LICENSES_UPDATE_FAILED,
  LICENSE_ACTION_LABELS,
  lockedLicensesNote,
  lockedUnlicensedNote,
  manageInvitationLicensesDescription,
  manageInvitationLicensesTitle,
  manageLicensesLabel,
} from 'constants/licenses';
import {
  ASSIGN_TOO_MANY_SKILL_DIVE_TITLE,
  SKILL_DIVE_ACTION_LABELS,
  SKILL_DIVE_LICENSES_NO_LONGER_CHANGEABLE,
  SKILL_DIVE_LICENSES_UPDATED,
  SKILL_DIVE_LICENSES_UPDATE_FAILED,
  SKILL_DIVE_NON_TRANSFERABLE_NOTE,
  SKILL_DIVE_REMOVED_WITH_LICENSE_NOTE,
  assignSkillDiveDescription,
  assignSkillDiveTitle,
  assignTooManySkillDiveDescription,
  removeSkillDiveDescription,
  removeSkillDiveTitle,
  skippedWithoutTrainingNote,
} from 'constants/skill_dive';
import {
  LICENSE_REJECTION_OUTCOMES,
  licensesNotChanged,
} from 'constants/errors/license_error_codes';
import { UserInvites } from 'contexts/users/types';
import { ACC_OWNER } from 'constants/roles';
import { isRoleManager, licenseRoleLock } from 'components/usersTable/helper';

const holdsSkillDive = (invite: UserInvites) => Boolean(findAssignedSkillDive(invite));

// A request releases one add-on's seats, and each held seat names its own add-on (a seat can
// outlive its pool), so releasing sends one request per add-on.
const releaseBatches = (holders: UserInvites[]) => {
  const seatAddon = (holder: UserInvites) => findAssignedSkillDive(holder)?.addon;
  return Array.from(new Set(holders.map(seatAddon).filter(Boolean))).map((addon) => ({
    addon,
    ids: holders.filter((holder) => seatAddon(holder) === addon).map(({ id }) => id),
  }));
};

const UserHeaderFilters: React.FC = (): JSX.Element => {
  const {
    account,
    user: userSession,
    licenses_available,
    updateLicensesAvailables,
    allows: { can_delete_user, can_unnasign_license },
    actions: sessionActions,
    userRoleIds,
    isStaff,
  } = useContext(SessionContext);
  const notifyLicenseRejection = useLicenseRejection();
  const isAccountOwner = validateAccountOwner(userRoleIds);
  const skillDiveFlow = useBooleanFlag(FEATURE_FLAGS.SKILL_DIVE_LICENSE_FLOW);
  const canInviteUsers = skillDiveFlow ? isTeamManagerOrAccountOwner(userRoleIds) : isAccountOwner;
  const { selectedRows, filters, actions, table } = useContext(UsersContext);
  const countRows = selectedRows?.length || 0;
  const skillDivePool = account?.account_available_addons?.find(
    ({ name }) => name === CONTENT_TYPES.SKILL_DIVE
  );
  const skillDiveAvailability = useLicenseAvailability(skillDivePool);
  // Whether the signed-in user can't change this row's license.
  const isLicenseLocked = (row: UserInvites) =>
    Boolean(
      licenseRoleLock(
        {
          uaaId: row.uaa_id,
          email: row.email,
          isAccountOwner: row.uaa_data
            ? Boolean(row.uaa_data.roles?.data?.some((role) => role.name === ACC_OWNER))
            : row.role === ACC_OWNER,
          isTeamManager: isRoleManager(row.role, row.teams ?? []),
        },
        {
          uaaId: userSession?.uaa_id,
          email: userSession?.email,
          isStaff: Boolean(isStaff),
          isAccountOwner,
        }
      )
    );

  // Splits the selected rows by whether their license can change, and the changeable ones by the
  // Skill Dive change each can take. Runs every render, since a row's role can change while it
  // stays selected.
  const splitLicenseSelection = (rows: UserInvites[], ids: string[]) => {
    const selected = rows.filter((item) => ids.includes(item.id));
    const changeable = selected.filter((item) => !isLicenseLocked(item));
    const locked = selected.filter(isLicenseLocked);
    return {
      selected,
      locked,
      changeableIds: changeable.map(({ id }) => id),
      withLicense: changeable.filter((item) => item.license),
      noLicense: changeable.filter((item) => !item.license),
      withSkillDive: changeable.filter(holdsSkillDive),
      canReserveSkillDive: changeable.filter((item) => item.license && !holdsSkillDive(item)),
    };
  };
  const licenseSelection = splitLicenseSelection(table?.rows ?? [], selectedRows);
  const {
    locked: lockedInvites,
    changeableIds,
    withLicense,
    noLicense,
    withSkillDive,
    canReserveSkillDive,
  } = licenseSelection;
  // The latest rows and split, for a modal whose handlers came from an earlier render.
  const latestLicenseSelection = useRef({ rows: table?.rows ?? [], split: licenseSelection });
  latestLicenseSelection.current = { rows: table?.rows ?? [], split: licenseSelection };

  const [actionModalOpen, setActionModalOpen] = useState<boolean>(false);
  const [blockedActions, setBlockedActions] = useState<boolean>(false);
  const [isEditingInvite, setEditingInvite] = useState<boolean>(false);
  const [actionModalOptions, setActionModalOptions] = useState<{
    title: string;
    description: string | ReactNode;
    buttons: Array<{
      disabled?: boolean;
      label: string;
      type: string;
      onClick: () => void;
    }>;
    onCancel: () => void;
  }>({
    title: '',
    description: '',
    buttons: [],
    onCancel: null,
  });

  useEffect(() => {
    setActionModalOpen(!actionModalOpen);
  }, [actionModalOptions]);

  useEffect(() => {
    if (!selectedRows.length) setActionModalOpen(false);
  }, [selectedRows]);

  const RemoveUsersModal = ({ total, onAccept }) => {
    setActionModalOptions({
      title: `Remove user${total > 1 ? 's' : ''}?`,
      description: `Are you sure you want to remove ${total} selected user${total > 1 ? 's' : ''}?`,
      onCancel: () => {
        setActionModalOpen(false);
      },
      buttons: [
        { label: 'Cancel', type: 'secondary', onClick: () => setActionModalOpen(false) },
        {
          label: `Remove user${total > 1 ? 's' : ''}`,
          type: 'primary',
          onClick: () => {
            onAccept();
            setActionModalOpen(false);
          },
        },
      ],
    });
  };

  const handleRemoveUsers = () => {
    RemoveUsersModal({
      total: selectedRows.length,
      onAccept: () => actions?.onRemoveTableItem(selectedRows, 'Successfully removed users'),
    });
  };

  // Locked users stay as they are: those with a license keep it, those without don't get one.
  const lockedWithLicenseNote = lockedLicensesNote(
    lockedInvites.filter((invite) => invite.license).length
  );
  const lockedWithoutLicenseNote = lockedUnlicensedNote(
    lockedInvites.filter((invite) => !invite.license).length
  );

  const setLicenses = async ({ assign }: { assign: boolean }) => {
    if (!blockedActions) {
      // Send only users who were changeable when the modal opened and still are.
      const { rows, split: current } = latestLicenseSelection.current;
      const ids = changeableIds.filter((id) => current.changeableIds.includes(id));
      if (!ids.length) {
        Notification({ text: LICENSES_NO_LONGER_CHANGEABLE, type: NOTIFICATION_TYPES.ERROR });
        setActionModalOpen(false);
        return;
      }
      setBlockedActions(true);
      const response = await setUserLicense({ ids, license: assign });
      if (!(response instanceof Error) && response?.result) {
        // Count only users whose license actually changes.
        const loop: number = current.selected.filter(
          (item) => ids.includes(item.id) && (assign ? !item.license : Boolean(item.license))
        ).length;
        for (let index = 0; index < loop; index++) {
          updateLicensesAvailables(assign, true);
        }
        Notification({ text: LICENSES_UPDATED, type: NOTIFICATION_TYPES.SUCCESS });
        const newTableRows = rows.map((item) => {
          if (ids.includes(item.id)) {
            return {
              ...item,
              license: assign,
            };
          } else {
            return item;
          }
        });
        actions?.onTableUpdate([]);
        actions?.onTableUpdate(newTableRows);
        const releasedSkillDive = current.selected.some(
          (item) => ids.includes(item.id) && holdsSkillDive(item)
        );
        if (!assign && releasedSkillDive) {
          actions?.onRefreshData();
          sessionActions.refreshSession();
          sessionActions.refreshAccounts();
        }
      } else {
        notifyLicenseRejection(response, {
          outcome: licensesNotChanged(assign),
          fallback: LICENSES_UPDATE_FAILED,
          onRefresh: actions?.onRefreshData,
        });
      }
      setBlockedActions(false);
      setActionModalOpen(false);
    }
  };

  const RemoveLicensesModal = () => {
    setActionModalOptions({
      title: `Remove User${withLicense.length > 1 ? 's' : ''} Licenses`,
      description: `Are you sure you want to remove license for ${
        withLicense.length
      } selected users?${
        withLicense.some(holdsSkillDive) ? SKILL_DIVE_REMOVED_WITH_LICENSE_NOTE : ''
      }${lockedWithLicenseNote}`,
      onCancel: () => {
        setActionModalOpen(false);
      },
      buttons: [
        { label: 'Cancel', type: 'secondary', onClick: () => setActionModalOpen(false) },
        {
          label: 'Remove License',
          type: 'primary',
          onClick: () => {
            setLicenses({ assign: false });
          },
        },
      ],
    });
  };

  const AssignLicensesModal = () => {
    let title, description, acceptLabel;

    if (noLicense.length > 1) {
      title = `Assign Users Licenses`;
      description = `Are you sure you want to assign license for ${noLicense.length} selected users?${lockedWithoutLicenseNote}`;
      acceptLabel = 'Assign Licenses';
    } else {
      title = `Assign User License`;
      description = `Are you sure you want to assign license for ${noLicense[0].email}?${lockedWithoutLicenseNote}`;
      acceptLabel = 'Assign License';
    }

    setActionModalOptions({
      title,
      description,
      onCancel: () => setActionModalOpen(false),
      buttons: [
        { label: 'Cancel', type: 'secondary', onClick: () => setActionModalOpen(false) },
        {
          label: acceptLabel,
          type: 'primary',
          onClick: () => {
            setLicenses({ assign: true });
          },
        },
      ],
    });
  };

  const AssignALotLicensesModal = () => {
    setActionModalOptions({
      title: `Assign User${noLicense.length > 1 ? 's' : ''} Licenses`,
      description: `You selected ${noLicense.length} users, but you only have ${licenses_available} licenses available. Please, choose fewer than ${noLicense.length} users to continue.`,
      onCancel: () => setActionModalOpen(false),
      buttons: [{ label: 'Ok', type: 'secondary', onClick: () => setActionModalOpen(false) }],
    });
  };

  const setInvitesSkillDive = async ({ assign }: { assign: boolean }) => {
    if (blockedActions) return;
    // Only invitations that could take the change when the modal opened and still can: the
    // backend rejects the whole batch otherwise.
    const opened = (assign ? canReserveSkillDive : withSkillDive).map(({ id }) => id);
    const { canReserveSkillDive: canReserve, withSkillDive: holders } =
      latestLicenseSelection.current.split;
    const ids = (assign ? canReserve : holders)
      .map(({ id }) => id)
      .filter((id) => opened.includes(id));
    const batches = assign
      ? [{ addon: skillDivePool?.id, ids }]
      : releaseBatches(holders.filter(({ id }) => ids.includes(id)));
    if (!ids.length || !batches.length || batches.some(({ addon }) => !addon)) {
      Notification({
        text: SKILL_DIVE_LICENSES_NO_LONGER_CHANGEABLE,
        type: NOTIFICATION_TYPES.ERROR,
      });
      setActionModalOpen(false);
      return;
    }
    setBlockedActions(true);
    const responses = await Promise.all(
      batches.map((batch) => setPendingInvitesAddon({ ...batch, assign }))
    );
    const applied = responses.filter(
      (response) => !(response instanceof Error) && response?.result
    ).length;
    let refetched = false;
    if (applied === responses.length) {
      Notification({ text: SKILL_DIVE_LICENSES_UPDATED, type: NOTIFICATION_TYPES.SUCCESS });
    } else {
      // A refusal with a license code refetches the rows and the counts itself.
      refetched = Boolean(
        notifyLicenseRejection(
          responses.find((response) => response instanceof Error || !response?.result),
          {
            // Some invitations did change when another add-on's batch went through.
            outcome: applied ? undefined : LICENSE_REJECTION_OUTCOMES.NO_INVITATIONS_CHANGED,
            fallback: SKILL_DIVE_LICENSES_UPDATE_FAILED,
            onRefresh: actions?.onRefreshData,
          }
        )
      );
    }
    // A batch that went through changed the counts, even when another was refused.
    if (applied > 0 && !refetched) {
      actions?.onRefreshData();
      sessionActions.refreshSession();
      sessionActions.refreshAccounts();
    }
    setBlockedActions(false);
    setActionModalOpen(false);
  };

  const AssignSkillDiveModal = () => {
    const total = canReserveSkillDive.length;
    const skipped = changeableIds.length - total - withSkillDive.length;
    const skippedNote = skippedWithoutTrainingNote(skipped);
    const lockedSkillDiveNote = lockedLicensesNote(
      lockedInvites.filter((invite) => invite.license && !holdsSkillDive(invite)).length
    );
    // The row switch gives the same warning before assigning on a plan that can't take seats back.
    const nonTransferableNote = can_unnasign_license ? '' : SKILL_DIVE_NON_TRANSFERABLE_NOTE;
    setActionModalOptions({
      title: assignSkillDiveTitle(total),
      description: [
        assignSkillDiveDescription(total),
        skippedNote,
        lockedSkillDiveNote,
        nonTransferableNote,
      ].join(''),
      onCancel: () => setActionModalOpen(false),
      buttons: [
        {
          label: ACTION_MODAL_LABELS.CANCEL,
          type: ACTION_MODAL_BUTTON_TYPES.SECONDARY,
          onClick: () => setActionModalOpen(false),
        },
        {
          label: SKILL_DIVE_ACTION_LABELS.ASSIGN,
          type: ACTION_MODAL_BUTTON_TYPES.PRIMARY,
          onClick: () => {
            setInvitesSkillDive({ assign: true });
          },
        },
      ],
    });
  };

  const AssignTooManySkillDiveModal = () => {
    setActionModalOptions({
      title: ASSIGN_TOO_MANY_SKILL_DIVE_TITLE,
      description: assignTooManySkillDiveDescription(
        canReserveSkillDive.length,
        skillDiveAvailability.value ?? 0
      ),
      onCancel: () => setActionModalOpen(false),
      buttons: [
        {
          label: ACTION_MODAL_LABELS.OK,
          type: ACTION_MODAL_BUTTON_TYPES.SECONDARY,
          onClick: () => setActionModalOpen(false),
        },
      ],
    });
  };

  const RemoveSkillDiveModal = () => {
    const total = withSkillDive.length;
    const lockedSkillDiveNote = lockedLicensesNote(lockedInvites.filter(holdsSkillDive).length);
    setActionModalOptions({
      title: removeSkillDiveTitle(total),
      description: `${removeSkillDiveDescription(total)}${lockedSkillDiveNote}`,
      onCancel: () => setActionModalOpen(false),
      buttons: [
        {
          label: ACTION_MODAL_LABELS.CANCEL,
          type: ACTION_MODAL_BUTTON_TYPES.SECONDARY,
          onClick: () => setActionModalOpen(false),
        },
        {
          label: SKILL_DIVE_ACTION_LABELS.REMOVE,
          type: ACTION_MODAL_BUTTON_TYPES.PRIMARY,
          onClick: () => {
            setInvitesSkillDive({ assign: false });
          },
        },
      ],
    });
  };

  const getNumericLicensesAvailable = () =>
    typeof licenses_available === 'number'
      ? licenses_available
      : licenses_available
      ? Number.POSITIVE_INFINITY
      : 0;

  const pendingLicenseOptions = () => {
    const numericLicensesAvailable = getNumericLicensesAvailable();
    // As the row switch: no new seats when Skill Dive isn't on the plan or the pool is used up.
    const skillDiveAssignable =
      skillDiveAvailability.mode !== 'not_on_plan' &&
      !(skillDiveAvailability.mode === 'count' && (skillDiveAvailability.value ?? 0) <= 0);
    const options: Array<{ label: string; open: () => void }> = [];
    // As the row switch: no new licenses once none are left.
    if (noLicense.length > 0 && numericLicensesAvailable > 0) {
      options.push({
        label: LICENSE_ACTION_LABELS.ASSIGN,
        open: () =>
          noLicense.length > numericLicensesAvailable
            ? AssignALotLicensesModal()
            : AssignLicensesModal(),
      });
    }
    if (withLicense.length > 0 && can_unnasign_license) {
      options.push({ label: LICENSE_ACTION_LABELS.REMOVE, open: RemoveLicensesModal });
    }
    if (canReserveSkillDive.length > 0 && skillDiveAssignable) {
      options.push({
        label: SKILL_DIVE_ACTION_LABELS.ASSIGN,
        open: () =>
          skillDiveAvailability.mode === 'count' &&
          canReserveSkillDive.length > skillDiveAvailability.value
            ? AssignTooManySkillDiveModal()
            : AssignSkillDiveModal(),
      });
    }
    if (withSkillDive.length > 0 && can_unnasign_license) {
      options.push({ label: SKILL_DIVE_ACTION_LABELS.REMOVE, open: RemoveSkillDiveModal });
    }
    return options;
  };

  const handleManagePendingLicenses = () => {
    const options = pendingLicenseOptions();
    if (options.length === 1) {
      options[0].open();
      return;
    }
    setActionModalOptions({
      title: manageInvitationLicensesTitle(selectedRows.length),
      description: manageInvitationLicensesDescription(selectedRows.length),
      onCancel: () => setActionModalOpen(false),
      buttons: options.map(({ label, open }) => ({
        label,
        type: ACTION_MODAL_BUTTON_TYPES.PRIMARY,
        onClick: () => {
          setActionModalOpen(false);
          open();
        },
      })),
    });
  };

  const handleManageLicenses = () => {
    const numericLicensesAvailable = getNumericLicensesAvailable();
    if (withLicense.length > 0 && noLicense.length > 0) {
      setActionModalOptions({
        title: `Manage User${changeableIds.length > 1 ? 's' : ''} Licenses`,
        description: `Would you like to remove or assign licenses for ${changeableIds.length} selected users?${lockedWithLicenseNote}`,
        onCancel: () => {
          setActionModalOpen(false);
        },
        buttons: [
          {
            label: 'Assign License',
            type: 'primary',
            onClick: () => {
              setActionModalOpen(false);
              noLicense.length > numericLicensesAvailable
                ? AssignALotLicensesModal()
                : AssignLicensesModal();
            },
          },
          {
            label: 'Remove License',
            type: 'primary',
            onClick: () => {
              setActionModalOpen(false);
              RemoveLicensesModal();
            },
          },
        ],
      });
    } else {
      if (withLicense.length === changeableIds.length) {
        RemoveLicensesModal();
      } else if (noLicense.length > numericLicensesAvailable) {
        AssignALotLicensesModal();
      } else {
        AssignLicensesModal();
      }
    }
  };

  const handleResendInvitations = async () => {
    const response = await resendUserInvites({ ids: selectedRows });
    if (!(response instanceof Error) && response.result) {
      Notification({ text: 'Invitations successfully resent', type: 'success' });
    } else {
      Notification({ text: 'Invitations couldn’t be updated', type: 'error' });
    }
  };

  const handleCancelInvitations = () => {
    let title, description, acceptLabel;

    if (selectedRows.length > 1) {
      title = 'Cancel Invites';
      description = `Are you sure you want to cancel the invites for these ${selectedRows.length} selected users?`;
      acceptLabel = 'Remove Invites';
    } else {
      title = 'Cancel Invite';
      description = `Are you sure you want to cancel ${
        table.rows.find((row) => row.id === selectedRows[0]).email
      } invite?`;
      acceptLabel = 'Remove Invite';
    }

    setActionModalOptions({
      title,
      description,
      onCancel: () => {
        setActionModalOpen(false);
      },
      buttons: [
        { label: 'Cancel', type: 'secondary', onClick: () => setActionModalOpen(false) },
        {
          label: acceptLabel,
          type: 'primary',
          onClick: () => {
            actions?.onRemoveTableItem(selectedRows, 'Invitations successfully cancelled');
            setActionModalOpen(false);
          },
        },
      ],
    });
  };

  const showModalInviteUsers = () => {
    setEditingInvite(true);
  };

  const handleClose = (result?: string, invites?: Invite[]) => {
    setEditingInvite(false);
    if (result === 'ok' && invites) {
      actions.onCountsUpdate();
      const newLicensesValue =
        typeof licenses_available === 'boolean'
          ? licenses_available
          : licenses_available - invites.filter(({ has_license }) => has_license).length;
      sessionActions.updateLicenses(newLicensesValue);
      actions.onChangeFilters({ name: 'active', value: false }, false, true);
    }
  };

  return (
    <div className="users-header-filters" style={isDesktop ? { marginBottom: '7px' } : {}}>
      <div className="d-flex justify-content-between mw-100 p-md-2 align-items-center">
        <BrowserView>
          <div className="d-md-block">
            {filters.active === 'true' ? (
              <Title className="mb-0">Active Users</Title>
            ) : (
              <Title className="mb-0">Pending Users</Title>
            )}
            <SelectedCounts className="text-muted d-md-block mb-0">
              {countRows} users selected
            </SelectedCounts>
          </div>
        </BrowserView>
        <div className="col ms-md-8 bg-white" style={{ padding: '14px 8px' }}>
          <MobileView>
            <UserMobileTopBar />
          </MobileView>
          <div className="d-flex flex-wrap align-items-center">
            <SearchFilter />
            {countRows > 0 && filters.active === 'true' && (
              <div className="mt-4 mt-md-0 w-100 w-md-auto d-flex">
                <Button
                  disabled={!can_unnasign_license || !changeableIds.length}
                  $secondary
                  size={isMobile ? 'small' : null}
                  style={{ flexGrow: 1, margin: '0 4px' }}
                  onClick={handleManageLicenses}
                >
                  Manage license{selectedRows.length > 1 ? 's' : ''}
                </Button>
                <Button
                  disabled={!can_delete_user}
                  $secondary
                  size={isMobile ? 'small' : null}
                  style={{ flexGrow: 1, margin: '0 4px' }}
                  onClick={handleRemoveUsers}
                >
                  Remove user{selectedRows.length > 1 ? 's' : ''}
                </Button>
              </div>
            )}
            {countRows > 0 && filters.active === 'false' && (
              <>
                <div className="mt-4 mt-md-0 w-100 w-md-auto d-flex">
                  {skillDiveFlow && !isStaff && (
                    <Button
                      disabled={!pendingLicenseOptions().length}
                      $secondary
                      size={isMobile ? 'small' : null}
                      style={{ flexGrow: 1, margin: '0 4px' }}
                      onClick={handleManagePendingLicenses}
                    >
                      {manageLicensesLabel(selectedRows.length)}
                    </Button>
                  )}
                  <Button
                    $secondary
                    size={isMobile ? 'small' : null}
                    onClick={handleResendInvitations}
                    style={{ flexGrow: 1, margin: '0 4px' }}
                  >
                    Resend invitation
                  </Button>
                  <Button
                    $secondary
                    size={isMobile ? 'small' : null}
                    onClick={handleCancelInvitations}
                    style={{ flexGrow: 1, margin: '0 4px' }}
                  >
                    Cancel invitation
                  </Button>
                </div>
              </>
            )}
            {canInviteUsers && (
              <div className="btn-add-user">
                <Button
                  $primary={true}
                  onClick={showModalInviteUsers}
                  icon={'Plus'}
                  style={{ flexGrow: 1, margin: '0 4px' }}
                >
                  Add users
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
      <ActionModal
        loading={blockedActions}
        title={actionModalOptions.title}
        description={actionModalOptions.description}
        active={actionModalOpen}
        buttons={actionModalOptions.buttons}
        handleCancel={actionModalOptions.onCancel}
      />

      {isEditingInvite && (
        <InviteUsers account={account} isEditingInvite={isEditingInvite} onClose={handleClose} />
      )}
    </div>
  );
};

export default UserHeaderFilters;
